import { didApiUrl } from '@/environment';
import { authenticatedAxios } from '@/services/auth';
import {
    CreateAvatarRequest,
    CreateAvatarResponse,
    CreateDraftAvatarRequest,
    CreateDraftAvatarResponse,
    ExpressAvatarPayload,
    UpdateDraftAvatarRequest,
    UpdateDraftAvatarResponse,
} from '../types';

const createAvatar = async (payload: CreateAvatarRequest) => {
    const response = await authenticatedAxios.post<CreateAvatarResponse>(`${didApiUrl}/scenes/avatars`, payload);
    return response.data;
};

const createDraftAvatar = async (payload: CreateDraftAvatarRequest) => {
    const response = await authenticatedAxios.post<CreateDraftAvatarResponse>(`${didApiUrl}/scenes/avatars/draft`, payload);
    return response.data;
};

const updateDraftAvatar = async ({
    id,
    payload,
    generate = false,
}: {
    id: string;
    payload: UpdateDraftAvatarRequest;
    generate?: boolean;
}) => {
    const response = await authenticatedAxios.put<UpdateDraftAvatarResponse>(
        `${didApiUrl}/scenes/avatars/draft/${id}?generate=${generate}`,
        payload
    );
    return response.data;
};

/**
 * Creates express avatar.
 * If the avatar is a draft, it will generate the avatar. Otherwise, it will create a new avatar.
 * @param payload
 * @returns
 */
export const createExpressAvatar = async (
    payload: ExpressAvatarPayload
): Promise<{
    id: string;
}> => {
    if (payload.id) {
        return updateDraftAvatar({
            id: payload.id,
            payload: {
                source_url: payload.source_url,
                consent_id: payload.consent_id,
                thumbnail_url: payload.thumbnail_url,
                name: payload.name,
            },
            generate: true,
        });
    }

    if (!payload.source_url) {
        throw new Error('source_url is required');
    }

    return createAvatar({
        source_url: payload.source_url,
        consent_id: payload.consent_id,
        thumbnail_url: payload.thumbnail_url,
        is_greenscreen: payload.is_greenscreen ?? false,
        greenscreen: payload.greenscreen,
        name: payload.name,
    });
};

/**
 * Save an avatar as a draft.
 * If the avatar already exists, it will update the draft. Otherwise, it will create a new draft.
 * @param payload
 * @returns
 */
export const saveExpressDraftAvatar = async (payload: ExpressAvatarPayload): Promise<{ id: string }> => {
    if (payload.id) {
        return updateDraftAvatar({
            id: payload.id,
            payload: {
                source_url: payload.source_url,
                consent_id: payload.consent_id,
                thumbnail_url: payload.thumbnail_url,
                name: payload.name,
            },
        });
    }

    return createDraftAvatar({
        source_url: payload.source_url,
        consent_id: payload.consent_id,
        thumbnail_url: payload.thumbnail_url,
        name: payload.name,
    });
};
